import Icon from '@ant-design/icons';
import { Button, Descriptions, Space, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import LayoutComponent from '../../../../components/LayoutComponent/LayoutComponent';
import TaskStatusComponent from '../../../../components/TaskStatusComponent/TaskStatusComponent';
import { TaskSmartDelivery } from '../../../../model/entities';
import { TaskStatusType } from '../../../../model/types';
import { ReactComponent as BackSvg } from '../../../../resources/images/back.svg';
import { ReactComponent as RefreshSvg } from '../../../../resources/images/refresh.svg';
import tableService from '../../../../services/TableService';
import styles from './TaskSmartDeliveryPage.module.scss';
import taskSmartDeliveryApi from '../../../../api/TaskSmartDeliveryApi';
import notificationService from '../../../../services/NotificationService';
import { ExportOutlined } from '@ant-design/icons';

/**
 * Returns the task smart delivery page.
 * @returns the task smart delivery page.
 */
const TaskSmartDeliveryPage = (): React.ReactElement => {
    /*** HOOKS ***/

    const intl = useIntl();
    const params = useParams<ParamsType>();
    const [task, setTask] = useState<TaskSmartDelivery>();
    const [loading, setLoading] = useState<boolean>();

    /*** EFFECTS ***/

    const init = useCallback(async () => {
        try {
            setLoading(true);
            const task = await taskSmartDeliveryApi.get(+params.id);
            setTask(task);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    }, [intl, params.id]);

    useEffect(() => {
        init();
    }, [init]);

    /*** METHODS ***/

    /*** COMPONENTS ***/

    const items = task ? [task] : [];
    const columns: ColumnsType<TaskSmartDelivery> = [
        {
            title: <FormattedMessage id="taskSmartDelivery.projectId" />,
            dataIndex: 'projectId',
            key: 'projectId',
            width: 160,
            render: (value: string) => {
                if (value && task && task.projectId) {
                    const projectId = task.projectId?.replace('b.', '');
                    const url: string = `https://acc.autodesk.eu/docs/issues/projects/${projectId}`;
                    return (
                        <a href={url} target="_blank" rel="noreferrer noopener">
                            {value}
                            <ExportOutlined className={styles.navigationIcon} />
                        </a>
                    );
                } else {
                    return <></>;
                }
            },
        },
        {
            title: <FormattedMessage id="taskSmartDelivery.folderId" />,
            dataIndex: 'folderId',
            key: 'folderId',
            width: 160,
            render: (value: string) => {
                if (value && task && task.projectId) {
                    const projectId = task.projectId?.replace('b.', '');
                    const url: string = `https://acc.autodesk.eu/docs/files/projects/${projectId}?folderUrn=${value}`;
                    return (
                        <a href={url} target="_blank" rel="noreferrer noopener">
                            {value}
                            <ExportOutlined className={styles.navigationIcon} />
                        </a>
                    );
                } else {
                    return <></>;
                }
            },
        },
        {
            title: <FormattedMessage id="taskSmartDelivery.fileName" />,
            dataIndex: 'fileName',
            key: 'fileName',
        },
        {
            title: <FormattedMessage id="taskSmartDelivery.platform" />,
            dataIndex: ['taskSmartDeliveryPlatformFile', 'platform'],
            key: 'platform',
        },
        {
            title: <FormattedMessage id="taskSmartDelivery.provider" />,
            dataIndex: ['taskSmartDeliveryProviderFile', 'provider'],
            key: 'provider',
        },
        {
            title: <FormattedMessage id="taskSmartDelivery.audit.created" />,
            dataIndex: 'auditCreated',
            key: 'created',
            width: 200,
            align: 'center',
            render: (value: string, taskSmartDelivery: TaskSmartDelivery) => (
                <FormattedDate
                    value={taskSmartDelivery.audit!.created as any}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                    hour="2-digit"
                    minute="2-digit"
                    second="2-digit"
                />
            ),
        },
        {
            title: <FormattedMessage id="taskSmartDelivery.audit.updated" />,
            dataIndex: 'auditUpdated',
            key: 'updated',
            width: 200,
            align: 'center',
            render: (value: string, taskSmartDelivery: TaskSmartDelivery) => (
                <FormattedDate
                    value={taskSmartDelivery.audit!.updated as any}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                    hour="2-digit"
                    minute="2-digit"
                    second="2-digit"
                />
            ),
        },
        {
            title: <FormattedMessage id="taskSmartDelivery.status" />,
            dataIndex: 'status',
            key: 'status',
            width: 160,
            align: 'center',
            render: (status: TaskStatusType) => <TaskStatusComponent status={status} />,
        },
    ];

    return (
        <LayoutComponent pageId="tasks">
            <Descriptions
                title={
                    <>
                        <span className={styles.title}>
                            #{task?.id} - {task?.name}
                        </span>
                        <TaskStatusComponent status={task?.status} />
                        <Space>
                            <Tooltip title={<FormattedMessage id="button.back" />}>
                                <Link to="/tasks">
                                    <Button type="ghost" size="small" icon={<Icon component={BackSvg} />} />
                                </Link>
                            </Tooltip>
                            <Tooltip title={<FormattedMessage id="button.refresh" />}>
                                <Button
                                    type="ghost"
                                    size="small"
                                    icon={<Icon component={RefreshSvg} />}
                                    onClick={init}
                                />
                            </Tooltip>
                        </Space>
                    </>
                }
                column={2}
                size="small"
                className={styles.summary}
            >
                <Descriptions.Item label={<FormattedMessage id="taskSmartDelivery.fileName" />} span={2}>
                    {task?.fileName}
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="task.audit.created" />}>
                    <FormattedDate
                        value={task?.audit?.created as any}
                        day="2-digit"
                        month="2-digit"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        second="2-digit"
                    />
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="task.audit.updated" />}>
                    <FormattedDate
                        value={task?.audit?.updated as any}
                        day="2-digit"
                        month="2-digit"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        second="2-digit"
                    />
                </Descriptions.Item>
                <Descriptions.Item>{task?.description}</Descriptions.Item>
            </Descriptions>
            <Table
                dataSource={items}
                columns={columns}
                pagination={tableService.createMemoryPagination(items, undefined, true)}
                rowKey="id"
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading}
            />
        </LayoutComponent>
    );
};

export default TaskSmartDeliveryPage;

type ParamsType = { id: string };
